import React, { useState } from 'react';
import { Card } from '../ui/Card.tsx';
import { Button } from '../ui/Button.tsx';
import { CreditCard, ExternalLink, Crown, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createStripePortalSession, formatSubscriptionStatus, getBillingPeriodInfo } from '../../services/billingService.ts';
import type { UserProfile } from '../../hooks/useUserProfile.ts';

interface BillingSectionProps {
  profile: UserProfile;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const BillingSection: React.FC<BillingSectionProps> = ({ 
  profile, 
  onSuccess, 
  onError 
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  console.log('[BillingSection] Rendering with profile:', {
    isPro: profile.isPro,
    hasSubscription: !!profile.subscription,
    loading: profile.loading,
  });

  const handleManageBilling = async () => {
    console.log('[BillingSection] Opening Stripe portal...');
    setLoading(true);
    
    try {
      const portalUrl = await createStripePortalSession();
      console.log('[BillingSection] Portal URL received, opening...');
      window.location.href = portalUrl;
      onSuccess?.();
    } catch (err) {
      console.error('[BillingSection] Failed to open billing portal:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to open billing portal';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToPro = () => {
    console.log('[BillingSection] Navigating to upgrade page...');
    navigate('/upgrade');
  };

  if (profile.loading) {
    return (
      <Card className="p-6">
        <div className="h-6 bg-gray-200 rounded-md w-40 mb-4 animate-pulse"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded-md w-full animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded-md w-3/4 animate-pulse"></div>
        </div>
        <div className="mt-4 h-10 bg-gray-200 rounded-md animate-pulse"></div>
      </Card>
    );
  }

  const subscription = profile.subscription;
  const subscriptionStatus = formatSubscriptionStatus(subscription);
  const billingInfo = getBillingPeriodInfo(subscription);

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
        <CreditCard className="h-5 w-5 mr-2 text-green-400" />
        Plan & Billing
      </h3>
      
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-400">Current Plan</span>
          <div className="flex items-center">
            {profile.isPro && <Crown className="h-4 w-4 text-yellow-400 mr-1" />}
            <span className="text-sm font-semibold text-gray-200">
              {profile.isPro ? 'Pro' : 'Free'}
            </span>
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-400">Status</span>
          <span 
            className={`text-sm font-medium ${
              subscriptionStatus.color === 'green' ? 'text-green-400' :
              subscriptionStatus.color === 'orange' ? 'text-orange-400' :
              subscriptionStatus.color === 'red' ? 'text-red-400' :
              'text-gray-400'
            }`}
          >
            {subscriptionStatus.status}
          </span>
        </div>

        {subscription && billingInfo && (
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-400 flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              Next Billing
            </span>
            <span className="text-sm text-gray-200">
              {billingInfo.formattedEndDate}
              {billingInfo.daysRemaining <= 7 && billingInfo.daysRemaining > 0 && (
                <span className="text-orange-400 ml-1">
                  ({billingInfo.daysRemaining} days)
                </span>
              )}
            </span>
          </div>
        )}

        {subscription && (
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-400">Subscription ID</span>
            <span className="text-xs font-mono text-gray-400 bg-gray-700 px-2 py-1 rounded">
              {subscription.stripe_subscription_id?.slice(-8) || 'N/A'}
            </span>
          </div>
        )}
      </div>

      <div className="mt-6">
        {profile.isPro ? (
          <Button
            onClick={handleManageBilling}
            disabled={loading}
            className="w-full flex items-center justify-center"
            variant="outline"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {loading ? 'Opening...' : 'Manage Billing'}
          </Button>
        ) : (
          <Button
            onClick={handleUpgradeToPro}
            disabled={loading}
            className="w-full flex items-center justify-center"
          >
            <Crown className="h-4 w-4 mr-2" />
            Upgrade to Pro
          </Button>
        )}
      </div>

      {!profile.isPro && (
        <div className="mt-4 p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
          <p className="text-sm text-blue-300">
            Upgrade to Pro for unlimited scans, advanced security checks, and priority support.
          </p>
        </div>
      )}
    </Card>
  );
}; 